import capitalize from 'lodash/capitalize';

export const URL_REGEX = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
export const PHONE_NUMBER_REGEX = /^\(\d{3}\) \d{3}-\d{4}( ext \d+)?$/;

export const pluralize = (text: string, count: number | '--') => {
  if (typeof count === 'number' && (count === 0 || count > 1) && text === 'deficiency') {
    return 'deficiencies';
  }
  if (count === '--') return text;
  if (count > 1) return `${text}s`;
  return text;
};

export const isValidJSON = (value: string) => {
  try {
    JSON.parse(value);
    return true;
  } catch (error) {
    return false;
  }
};

/*
  Test if the argument str is empty, which includes newline & blank spaces.
*/
export const isEmptyString = (str: string) => !/\S/.test(str);

/*
  When toTitleCase is false:
    Input: home_inspection
    Output: Home inspection
  When toTitleCase is true:
    Input: home_inspection
    Output: Home Inspection
*/
export const getFormattedString = (str: string, toTitleCase = false) => {
  if (!str || str.length === 0) return str;
  const ret = ((str === 're_inspection' || str === 're_visit') ? capitalize(str.replace(/_/g, '-')) : capitalize(str.replace(/_/g, ' ')));
  if (!toTitleCase) return ret;
  const strSplit = ret.split(' ');
  for (let i = 0; i < strSplit.length; i += 1) {
    if (strSplit[i]) {
      strSplit[i] = strSplit[i][0].toUpperCase() + strSplit[i].substring(1);
    }
  }

  return strSplit.join(' ');
};

/*
  Example:
  Input: arr = ["home_inspection", "re_inspection", "rent_ready_inspection"]
  Output: "Home inspection, Re-inspection, Rent ready inspection"
*/
export const stringListBuilder = (arr: (string | null)[]): string => {
  const validStrings = arr.filter((str) => str && str !== '') as string[];
  return validStrings.map((str) => (getFormattedString(str))).join(', ');
};

// From: https://stackoverflow.com/questions/64254355/cut-string-into-chunks-without-breaking-words-based-on-max-length
/*
   Input:
      n = 16
      str = 'The quick brown fox jumps over the lazy dog'
  Output: ["The quick brown", "fox jumps over", "the lazy dog"]
*/
export const splitString = (n: number, str: string) => {
  const arr = str?.split(' ');
  const result = [];
  let subStr = arr[0];
  for (let i = 1; i < arr.length; i += 1) {
    const word = arr[i];
    if (subStr.length + word.length + 1 <= n) {
      subStr = `${subStr} ${word}`;
    } else {
      result.push(subStr);
      subStr = word;
    }
  }
  if (subStr.length) {
    result.push(subStr);
  }
  return result;
};

/*
  Accepts any string input and removes any non-valid phone digits
  Returns only the first 10 integers as a string
*/
export const unformatPhoneNumber = (phoneNumber: string) => phoneNumber.replace(/\D/g, '').substring(0, 10);

/*
  Accepts any string input and removes any non-valid phone digits
  Returns the phone number input formatted as (XXX) XXX-XXXX
*/
export const getFormattedPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return phoneNumber;
  const digitsOnly = unformatPhoneNumber(phoneNumber);

  if (digitsOnly.length < 4) return digitsOnly;
  if (digitsOnly.length < 7) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  }

  return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
};

/* Format phone number for Amplify
 function assumes the length of the phone number string is 10 or 11 */
export const formatPhoneNumberAmplify = (phoneNumber: string) => {
  if (phoneNumber.length === 11) {
    return `+${phoneNumber}`;
  }
  return `+1${phoneNumber}`;
};

export const formatPhoneNumberWithUSCountryCode = (phoneNumber: string) => {
  return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '+1 ($1) $2-$3');
};

export const isHelloInspectifyEmail = (email: string) => {
  if (email.indexOf('hello') !== -1
    && email.indexOf('@inspectify.com') !== -1) {
    return true;
  }
  return false;
};

export const formatURL = (url: string) => {
  let retURL = url;
  if (!url.startsWith('www') && !url.startsWith('https://')) {
    retURL = `https://www.${retURL}`;
  }
  if (url.startsWith('www')) {
    retURL = `https://${retURL}`;
  }
  return retURL;
};

export const convertUrlToFilename = (str : string) => {
  if (!str || str.length <= 1) return '';
  return (str.split('/').pop()?.replace(/%20/g, ' '));
};

// Remove the underscores and capitalize every first letter
export const capitalizeWords = (str: string) => str?.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());

export const toCapitalizedSentence = (array: string[]): string => {
  if (!array || array.length === 0) return '';

  const capitalizedArray = array.map(capitalizeWords);

  // put "Home Inspection" first
  const homeInspectionIndex = capitalizedArray.indexOf('Home Inspection');
  if (homeInspectionIndex !== -1) {
    capitalizedArray.splice(homeInspectionIndex, 1);
    capitalizedArray.unshift('Home Inspection');
  }
  // Join every word by a comma then replace the last comma with the word "and"
  return capitalizedArray.join(', ').replace(/,([^,]*)$/, ' and$1');
};

/*
  Accepts any string input
  Returns true if the email follows the pattern outlined here:
  https://www.scaler.com/topics/email-validation-in-javascript/
*/
export const validateEmail = (email: string) : boolean => {
  const regex = /^\w+([.\-+]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
  return regex.test(email);
};

/*
  Accepts any string input
  Returns true if the zip code follows US format for either XXXXX or XXXXX-XXXX
  OR returns true if the zip code follows Canadian format for either XXX XXX or XXX
*/
export const isValidZipCodeFormat = (zipCode: string) : boolean => {
  const regexUS = /^\d{5}(-\d{4})?$/;
  const regexCA = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z](?: \d[ABCEGHJ-NPRSTV-Z]\d)?$/;

  return regexUS.test(zipCode) || regexCA.test(zipCode);
};

export const capitalizeFirstLetter = (str: string) => {
  return str && str.length ? str.charAt(0).toUpperCase() + str.slice(1) : str;
};

export const capitalizeAllLetters = (str: string) => {
  if (!str) return ''
  return str.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};